import React, { useEffect, useState, Suspense, lazy } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Card, Tabs, Tab } from 'react-bootstrap';
import CustomerDetailsCard from './CustomerDetailsCard';
import AudioPlayer from './AudioPlayer';
import { Skeleton, List, Tooltip } from 'antd';
import { useEngagementStore } from "./store";
import './QaRecordingFeedback.css';
import { getCurrentUserRole, ROLES_NAME } from 'modules/utilities/utility';
import { QA_FORM_FILLED_PURPOSE } from 'config';


// Lazy load CallAnalysisQuestion and FeedbackForm components
const CallAnalysisQuestion = lazy(() => import('./CallAnalysisQuestion'));
const FeedbackForm = lazy(() => import('./FeedbackForm'));

const QaRecordingFeedback = () => {
  const { engagementTrackingId, engagementCoachUserId } = useParams();

  const { TEAM_LEAD, ADMIN, SUPER_ADMIN } = ROLES_NAME;
  // const currentUserTeam = secureLocalStorage.getItem("team");
  const currentUserRole = getCurrentUserRole();

  const [searchParams, setSearchParams] = useSearchParams();
  const [key, setKey] = useState(searchParams.get('tab') || 'questions');

  const [toggleState, setToggleState] = useState(false);

  const {
    fetchEngagementDetails,
    fetchScoringFormWithRecording,
    coachUserId,
    scoringFormData,
    engagementLoading,
    scoringFormLoading,
    callDetails,
  } = useEngagementStore();

  // If critical params are missing, don't render yet
  if (!engagementTrackingId) return null;

  useEffect(() => {
    setSearchParams({ tab: key });
  }, [key]);

  useEffect(() => {
    if (!engagementTrackingId) return;
    fetchEngagementDetails({ engagementTrackingId });
    fetchScoringFormWithRecording(key, toggleState, engagementTrackingId, engagementCoachUserId);
  }, [engagementTrackingId, key, toggleState]);

  if (engagementLoading || scoringFormLoading) {
    return (
      <Container fluid className="qa-recording-feedback    d-flex flex-column">
        <Row className="mb-3 flex-shrink-0">
          <Col sm={5}>
            <Card className="details-card">
              <Card.Body>
                <Skeleton active />
              </Card.Body>
            </Card>
          </Col>
          <Col sm={7} className="d-flex flex-column justify-content-center">
            <Card className="audio-card">
              <Card.Body className="d-flex flex-column align-items-center justify-content-center">
                <h4>Call Recording</h4>
                <Skeleton.Input style={{ width: 200 }} active />
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col>
            <Tabs id="qa-tabs" activeKey={key} className="mb-3">
              <Tab eventKey="questions" title="Questions">
                <List
                  itemLayout="horizontal"
                  dataSource={[{}, {}, {}, {}, {}]} // Placeholder data for skeleton
                  renderItem={() => (
                    <List.Item>
                      <Skeleton active title={false} paragraph={{ rows: 4 }} />
                    </List.Item>
                  )}
                />
              </Tab>
              <Tab eventKey="feedback" title="Feedback Form">
                <List
                  itemLayout="horizontal"
                  dataSource={[{}, {}, {}, {}, {}]} // Placeholder data for skeleton
                  renderItem={() => (
                    <List.Item>
                      <Skeleton active title={false} paragraph={{ rows: 4 }} />
                    </List.Item>
                  )}
                />
              </Tab>
            </Tabs>
          </Col>
        </Row>
      </Container>
    );
  }
  return (
    <Container fluid className="qa-recording-feedback d-flex flex-column">
      <Row className="mb-3 flex-shrink-0">
        <Col sm={5}>
          <Card className="details-card">
            <Card.Body className="m-0 p-0 d-flex align-items-center justify-content-center px-2">
              <CustomerDetailsCard />
            </Card.Body>
          </Card>
        </Col>
        <Col sm={7} className="d-flex flex-column justify-content-center">
          <Card className="audio-card">
            <Card.Body className="d-flex flex-column align-items-center justify-content-center">
              <h4>Call Recording</h4>
              <AudioPlayer src={callDetails?.recordingUrl} />
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col>
          <Tabs
            id="qa-tabs"
            activeKey={key}
            onSelect={(k) => setKey(k)}
            className="mb-3"
          >
            <Tab eventKey="questions" title="Questions" >
              <div className="">
                <Suspense fallback={
                  <List
                    itemLayout="horizontal"
                    dataSource={[{}, {}, {}, {}, {}]} // Placeholder data for skeleton
                    renderItem={() => (
                      <List.Item>
                        <Skeleton active title={false} paragraph={{ rows: 4 }} />
                      </List.Item>
                    )}
                  />
                }>
                  <CallAnalysisQuestion
                    scoringFormData={scoringFormData}
                    coachUserId={coachUserId}
                    engagementTrackingId={engagementTrackingId}
                    formType={QA_FORM_FILLED_PURPOSE?.FORM_AUDIT_BY_QA}
                    tabKey={'questions'}
                    toggleState={toggleState}
                    setToggleState={setToggleState}
                    callDetails={callDetails}
                  // callStartDate={callStartDate}
                  />
                </Suspense>
              </div>
            </Tab>

            <Tab
              eventKey="feedback"
              title={
                <Tooltip title={scoringFormData?.isFeedbackFormRequired ? "" : "Please fill the scoring form first"}>
                  Feedback Form
                </Tooltip>
              }
              disabled={!scoringFormData?.isFeedbackFormRequired}
            >
              <div className="tab-content-static">
                <Suspense fallback={<List
                  itemLayout="horizontal"
                  dataSource={[{}, {}, {}, {}, {}]} // Placeholder data for skeleton
                  renderItem={() => (
                    <List.Item>
                      <Skeleton active title={false} paragraph={{ rows: 4 }} />
                    </List.Item>
                  )}
                />}>
                  <FeedbackForm />
                </Suspense>
              </div>
            </Tab>

            <Tab eventKey="ata" title="ATA Form"  disabled={![TEAM_LEAD,ADMIN,SUPER_ADMIN].includes(currentUserRole)}>
              {<div>
                <Suspense fallback={
                  <List
                    itemLayout="horizontal"
                    dataSource={[{}, {}, {}, {}, {}]} // Placeholder data for skeleton
                    renderItem={() => (
                      <List.Item>
                        <Skeleton active title={false} paragraph={{ rows: 4 }} />
                      </List.Item>
                    )}
                  />
                }>
                  <CallAnalysisQuestion
                    scoringFormData={scoringFormData}
                    coachUserId={coachUserId}
                    engagementTrackingId={engagementTrackingId}
                    formType={QA_FORM_FILLED_PURPOSE?.FORM_AUDIT_BY_QA_TL}
                    tabKey={'ata'}
                    toggleState={toggleState}
                    setToggleState={setToggleState}
                    callDetails={callDetails}
                  // callStartDate={callStartDate}
                  />
                </Suspense>
              </div>}

            </Tab>

            <Tab eventKey="dispute" title="Dispute Form" disabled={![TEAM_LEAD,ADMIN,SUPER_ADMIN].includes(currentUserRole)}>
              {<div>
                <Suspense fallback={
                  <List
                    itemLayout="horizontal"
                    dataSource={[{}, {}, {}, {}, {}]} // Placeholder data for skeleton
                    renderItem={() => (
                      <List.Item>
                        <Skeleton active title={false} paragraph={{ rows: 4 }} />
                      </List.Item>
                    )}
                  />
                }>
                  <CallAnalysisQuestion
                    scoringFormData={scoringFormData}
                    coachUserId={coachUserId}
                    engagementTrackingId={engagementTrackingId}
                    formType={QA_FORM_FILLED_PURPOSE?.FORM_AUDIT_BY_QA_TL_BUT_DISPUTED_BY_COACH}
                    tabKey={'dispute'}
                    toggleState={toggleState}
                    setToggleState={setToggleState}
                    callDetails={callDetails}
                  // callStartDate={callStartDate}
                  />
                </Suspense>
              </div>}
            </Tab>
          </Tabs>
        </Col>
      </Row>
    </Container>
  );
};

export default QaRecordingFeedback;
